import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import Navbar from '../components/layout/Navbar';
import { motion } from 'framer-motion';

const Auth = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login, signup, user } = useAuth();
    const { toast } = useToast();

    const [isSignup, setIsSignup] = useState(searchParams.get('mode') === 'signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsSignup(searchParams.get('mode') === 'signup');
    }, [searchParams]);

    useEffect(() => {
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Client-side validation
        if (isSignup && password.length < 6) {
            const msg = 'Password must be at least 6 characters long';
            setError(msg);
            toast.warning(msg, { title: 'Weak Password' });
            setLoading(false);
            return;
        }

        if (!email.includes('@') || !email.includes('.')) {
            const msg = 'Please enter a valid email address';
            setError(msg);
            toast.warning(msg, { title: 'Invalid Email' });
            setLoading(false);
            return;
        }

        try {
            if (isSignup) {
                await signup(email, password, fullName);
                toast.success('Account created successfully! Logging you in...', { title: 'Welcome Aboard!' });
                // Auto login after signup or redirect to login? 
                // For smoother UX, let's login
                await login(email, password);
            } else {
                await login(email, password);
                toast.success('Welcome back! Redirecting to dashboard...', { title: 'Login Successful' });
            }
            navigate('/dashboard');
        } catch (err) {
            console.error(err);

            // Parse backend error message
            const backendDetail = err.response?.data?.detail || '';
            const statusCode = err.response?.status;

            let errorMessage = '';
            let errorTitle = isSignup ? 'Signup Failed' : 'Login Failed';

            // Handle specific error cases
            if (backendDetail.toLowerCase().includes('email already registered')) {
                errorMessage = 'This email is already registered. Please login or use a different email.';
                errorTitle = 'Email Already Exists';
            } else if (backendDetail.toLowerCase().includes('incorrect username or password') ||
                backendDetail.toLowerCase().includes('could not validate credentials')) {
                errorMessage = 'Invalid email or password. Please check your credentials and try again.';
                errorTitle = 'Invalid Credentials';
            } else if (statusCode === 401) {
                errorMessage = 'Authentication failed. Please check your email and password.';
                errorTitle = 'Access Denied';
            } else if (statusCode === 400) {
                errorMessage = backendDetail || 'Invalid request. Please check your input.';
                errorTitle = 'Invalid Input';
            } else if (statusCode === 500) {
                errorMessage = 'Server error. Please try again later.';
                errorTitle = 'Server Error';
            } else if (!err.response) {
                errorMessage = 'Unable to connect to server. Please check your internet connection.';
                errorTitle = 'Network Error';
            } else {
                errorMessage = backendDetail || 'Something went wrong. Please try again.';
            }

            setError(errorMessage);
            toast.error(errorMessage, { title: errorTitle });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cyber-black font-rajdhani">
            <Navbar />
            <div className="pt-32 pb-12 flex justify-center items-center px-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-cyber-gray p-8 rounded-2xl shadow-[0_0_20px_rgba(0,0,0,0.5)] w-full max-w-md border border-gray-800"
                >
                    <h2 className="text-3xl font-bold text-center text-white mb-8 font-orbitron">
                        {isSignup ? "Create an Account" : "Welcome Back"}
                    </h2>

                    {error && (
                        <div className="bg-red-900/20 text-red-400 p-3 rounded-lg mb-6 text-sm text-center border border-red-900/50">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {isSignup && (
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-cyber-black text-white focus:ring-2 focus:ring-neon-blue focus:border-neon-blue transition-all outline-none placeholder-gray-600"
                                    placeholder="John Doe"
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-cyber-black text-white focus:ring-2 focus:ring-neon-blue focus:border-neon-blue transition-all outline-none placeholder-gray-600"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Password</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-cyber-black text-white focus:ring-2 focus:ring-neon-blue focus:border-neon-blue transition-all outline-none placeholder-gray-600"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/30 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed font-orbitron tracking-wide"
                        >
                            {loading ? 'Processing...' : (isSignup ? 'Sign Up' : 'Sign In')}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-gray-400">
                            {isSignup ? "Already have an account?" : "Don't have an account?"}
                            <button
                                onClick={() => {
                                    setIsSignup(!isSignup);
                                    setError('');
                                    window.history.pushState({}, '', isSignup ? '/auth?mode=login' : '/auth?mode=signup');
                                }}
                                className="ml-2 text-neon-blue font-semibold hover:text-neon-purple transition-colors hover:underline"
                            >
                                {isSignup ? 'Log In' : 'Sign Up'}
                            </button>
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Auth;
